import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, AlertTriangle, CheckCircle, Loader2, Users, ShieldCheck, Database, Package, Building2, UserMinus } from "lucide-react";
import { API } from "@/App";
import { useTranslation } from "react-i18next";

export default function ResetDatabaseModule() {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "packages", "employees"]);

  const [loading, setLoading] = useState(false);
  const [loadingKeepUsers, setLoadingKeepUsers] = useState(false);
  const [confirmStep, setConfirmStep] = useState(0);
  const [confirmStepKeepUsers, setConfirmStepKeepUsers] = useState(0);
  const [result, setResult] = useState(null);
  
  // Business-specific data wipe
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState("");
  const [wipingBusinessData, setWipingBusinessData] = useState(false);
  const [businessWipeResult, setBusinessWipeResult] = useState(null);
  const [businessConfirmStep, setBusinessConfirmStep] = useState(0);
  const [loadingBusinesses, setLoadingBusinesses] = useState(true);
  
  // Full business reset (delete members, trainers, etc.)
  const [selectedBusinessFull, setSelectedBusinessFull] = useState("");
  const [fullResetConfirmStep, setFullResetConfirmStep] = useState(0);
  const [fullResetLoading, setFullResetLoading] = useState(false);
  const [fullResetResult, setFullResetResult] = useState(null);
  
  // User delete
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [deleteUserConfirmStep, setDeleteUserConfirmStep] = useState(0);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);
  const [deleteUserResult, setDeleteUserResult] = useState(null);
  const [deleteUserError, setDeleteUserError] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  
  // Business delete completely
  const [selectedBusinessDelete, setSelectedBusinessDelete] = useState("");
  const [deleteBusinessConfirmStep, setDeleteBusinessConfirmStep] = useState(0);
  const [deleteBusinessLoading, setDeleteBusinessLoading] = useState(false);
  const [deleteBusinessResult, setDeleteBusinessResult] = useState(null);

  useEffect(() => {
    fetchBusinesses();
    fetchUsers();
  }, []);

  const fetchBusinesses = async () => {
    try {
      const response = await fetch(`${API}/admin/businesses`, {
        credentials: "include"
      });
      if (response.ok) {
        setAllBusinesses(await response.json());
      }
    } catch (error) {
      console.error("Error fetching businesses:", error);
    } finally {
      setLoadingBusinesses(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/users`, {
        credentials: "include"
      });
      if (response.ok) {
        setAllUsers(await response.json());
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleReset = async () => {
    if (confirmStep < 2) {
      setConfirmStep(confirmStep + 1);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/reset-database`, {
        method: "POST",
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || t("admin:resetFailed"));
      }

      setResult(data);
      toast.success(t("admin:databaseReset"), { duration: 3000 });
      setConfirmStep(0);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetKeepUsers = async () => {
    if (confirmStepKeepUsers < 2) {
      setConfirmStepKeepUsers(confirmStepKeepUsers + 1);
      return;
    }

    setLoadingKeepUsers(true);
    try {
      const response = await fetch(`${API}/admin/reset-database-keep-users`, {
        method: "POST",
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || t("admin:resetFailed"));
      }

      setResult(data);
      toast.success(t("admin:databaseResetUsersKept"), { duration: 3000 });
      setConfirmStepKeepUsers(0);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingKeepUsers(false);
    }
  };

  // İşletme bazlı veri silme
  const handleBusinessWipe = async () => {
    if (!selectedBusiness) {
      toast.error(t('common:selectBusiness'));
      return;
    }

    if (businessConfirmStep < 1) {
      setBusinessConfirmStep(businessConfirmStep + 1);
      return;
    }

    setWipingBusinessData(true);
    setBusinessWipeResult(null);
    try {
      const response = await fetch(`${API}/businesses/${selectedBusiness}/wipe-data`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (response.ok) {
        const result = await response.json();
        setBusinessWipeResult(result);
        toast.success(t("common:toast_verilerBasariylaSilindi"));
        setBusinessConfirmStep(0);
      } else {
        const err = await response.json();
        toast.error(err.detail || t('members:deleteFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setWipingBusinessData(false);
    }
  };

  // İşletme TAM sıfırlama (üyeler, antrenörler dahil)
  const handleFullBusinessReset = async () => {
    if (!selectedBusinessFull) {
      toast.error(t('common:selectBusiness'));
      return;
    }

    if (fullResetConfirmStep < 2) {
      setFullResetConfirmStep(fullResetConfirmStep + 1);
      return;
    }

    setFullResetLoading(true);
    setFullResetResult(null);
    try {
      const response = await fetch(`${API}/businesses/${selectedBusinessFull}/full-reset`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (response.ok) {
        const result = await response.json();
        setFullResetResult(result);
        toast.success(t("common:toast_i̇sletmeVerileriTamamenSilindi"));
        setFullResetConfirmStep(0);
      } else {
        const err = await response.json();
        toast.error(err.detail || t('members:deleteFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setFullResetLoading(false);
    }
  };

  // Üye silme
  const handleDeleteUser = async () => {
    if (!selectedUser) {
      toast.error(t("common:toast_lutfenBirKullaniciSecin"));
      return;
    }

    if (deleteUserConfirmStep < 1) {
      setDeleteUserConfirmStep(deleteUserConfirmStep + 1);
      return;
    }

    setDeleteUserLoading(true);
    setDeleteUserResult(null);
    setDeleteUserError(null);
    try {
      const response = await fetch(`${API}/admin/users/${selectedUser}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setDeleteUserResult(data);
        toast.success(t("common:toast_kullaniciSilindi"));
        setDeleteUserConfirmStep(0);
        setSelectedUser("");
        fetchUsers(); // Refresh user list
      } else {
        // Handle the detailed error response
        if (data.detail && typeof data.detail === 'object') {
          setDeleteUserError(data.detail);
        } else {
          toast.error(data.detail || t('members:deleteFailed'));
        }
        setDeleteUserConfirmStep(0);
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setDeleteUserLoading(false);
    }
  };

  // İşletme tamamen silme
  const handleDeleteBusinessCompletely = async () => {
    if (!selectedBusinessDelete) {
      toast.error(t('common:selectBusiness'));
      return;
    }

    if (deleteBusinessConfirmStep < 2) {
      setDeleteBusinessConfirmStep(deleteBusinessConfirmStep + 1);
      return;
    }

    setDeleteBusinessLoading(true);
    setDeleteBusinessResult(null);
    try {
      const response = await fetch(`${API}/admin/businesses/${selectedBusinessDelete}/delete-completely`, {
        method: "DELETE",
        credentials: "include"
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setDeleteBusinessResult(data);
        toast.success(`${data.business_name} işletmesi tamamen silindi!`);
        setDeleteBusinessConfirmStep(0);
        setSelectedBusinessDelete("");
        fetchBusinesses(); // Refresh business list
      } else {
        toast.error(data.detail || t('members:deleteFailed'));
        setDeleteBusinessConfirmStep(0);
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setDeleteBusinessLoading(false);
    }
  };

  const resetConfirm = () => {
    setConfirmStep(0);
    setConfirmStepKeepUsers(0);
    setBusinessConfirmStep(0);
    setFullResetConfirmStep(0);
    setDeleteBusinessConfirmStep(0);
    setDeleteUserConfirmStep(0);
    setResult(null);
    setBusinessWipeResult(null);
    setFullResetResult(null);
    setDeleteUserResult(null);
    setDeleteUserError(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-navy flex items-center gap-2">
          <Database className="w-7 h-7" />
          Veritabanı Yönetimi
        </h1>
        <p className="text-grey mt-1">
          Veritabanı bakım ve sıfırlama işlemleri
        </p>
      </div>

      {/* İşletme Bazlı Veri Silme - En Üstte */}
      <Card className="border-2 border-orange-200 bg-orange-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-700">
            <Package className="w-5 h-5" />
            Paketler ve Detayları Sıfırlama
          </CardTitle>
          <CardDescription className="text-orange-600">
            Belirli bir işletmenin işlem verilerini siler. Üyeler ve personel silinmez.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingBusinesses ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
            </div>
          ) : (
            <>
              <div>
                <Label className="text-orange-800">{t("common:selectBusiness")}</Label>
                <Select value={selectedBusiness} onValueChange={(v) => {
                  setSelectedBusiness(v);
                  setBusinessConfirmStep(0);
                  setBusinessWipeResult(null);
                }}>
                  <SelectTrigger className="w-full mt-1 rounded-xl border-orange-300">
                    <SelectValue placeholder={t("common:ph_i̇sletmeSecin_e0f1")} />
                  </SelectTrigger>
                  <SelectContent>
                    {allBusinesses.map((b) => (
                      <SelectItem key={b.business_id} value={b.business_id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="p-3 bg-white rounded-lg border border-orange-200">
                <p className="text-sm font-medium text-orange-800 mb-2">Silinecek Veriler:</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-xs text-orange-700">
                  <div>• Paket satışları</div>
                  <div>• Tüm ödemeler</div>
                  <div>• Rezervasyonlar</div>
                  <div>• Primler (ödenen dahil)</div>
                  <div>• Maaş ödemeleri</div>
                  <div>• Giderler</div>
                  <div>• Kira borçları</div>
                  <div>• Hedefler</div>
                  <div>• Tahakkuklar</div>
                </div>
                <p className="text-xs text-green-700 mt-2 font-medium">
                  ✓ Üyeler ve personel korunur, sadece işlem geçmişleri sıfırlanır
                </p>
              </div>

              {/* Business Wipe Result */}
              {businessWipeResult && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Silinen Veriler:
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                    <div>Paket Satışları: <span className="font-semibold">{businessWipeResult.member_packages || 0}</span></div>
                    <div>Ödemeler: <span className="font-semibold">{businessWipeResult.payments || 0}</span></div>
                    <div>Rezervasyonlar: <span className="font-semibold">{businessWipeResult.reservations || 0}</span></div>
                    <div>Antrenör Primleri: <span className="font-semibold">{businessWipeResult.trainer_debts || 0}</span></div>
                    <div>Prim Ödemeleri: <span className="font-semibold">{businessWipeResult.trainer_debt_payments || 0}</span></div>
                    <div>Maaş Ödemeleri: <span className="font-semibold">{businessWipeResult.debt_payments || 0}</span></div>
                    <div>Giderler: <span className="font-semibold">{businessWipeResult.expenses || 0}</span></div>
                    <div>Bonus Ödemeleri: <span className="font-semibold">{businessWipeResult.bonus_payments || 0}</span></div>
                    <div>Çalışan Primleri: <span className="font-semibold">{businessWipeResult.employee_commissions || 0}</span></div>
                    <div>Hedefler: <span className="font-semibold">{businessWipeResult.goals || 0}</span></div>
                    <div>Üye Sıfırlanan: <span className="font-semibold">{businessWipeResult.members_reset || 0}</span></div>
                    <div>Antrenör Sıfırlanan: <span className="font-semibold">{businessWipeResult.trainers_reset || 0}</span></div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {businessConfirmStep === 0 && !businessWipeResult && (
                <Button
                  onClick={handleBusinessWipe}
                  className="bg-orange-600 hover:bg-orange-700 text-white w-full rounded-xl"
                  disabled={!selectedBusiness || wipingBusinessData}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Verileri Sıfırla
                </Button>
              )}

              {businessConfirmStep === 1 && (
                <div className="flex items-center gap-3">
                  <span className="text-orange-700 font-bold">⚠️ Bu işlem geri alınamaz!</span>
                  <Button
                    onClick={handleBusinessWipe}
                    className="bg-orange-700 hover:bg-orange-800 text-white rounded-xl"
                    disabled={wipingBusinessData}
                  >
                    {wipingBusinessData ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Onayla ve Sil
                  </Button>
                  <Button variant="outline" onClick={resetConfirm} className="rounded-xl">
                    İptal
                  </Button>
                </div>
              )}

              {businessWipeResult && (
                <Button variant="outline" onClick={resetConfirm} className="rounded-xl">
                  Tamam
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* İşletme TAM Sıfırlama - YENİ */}
      <Card className="border-2 border-red-200 bg-red-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700">
            <Building2 className="w-5 h-5" />
            İşletme Tam Sıfırlama
          </CardTitle>
          <CardDescription className="text-red-600">
            İşletmenin HER ŞEYİNİ siler! Sadece işletme kaydı, ayarları ve aboneliği kalır.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingBusinesses ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-red-600" />
            </div>
          ) : (
            <>
              <div>
                <Label className="text-red-800">{t("common:selectBusiness")}</Label>
                <Select value={selectedBusinessFull} onValueChange={(v) => {
                  setSelectedBusinessFull(v);
                  setFullResetConfirmStep(0);
                  setFullResetResult(null);
                }}>
                  <SelectTrigger className="w-full mt-1 rounded-xl border-red-300">
                    <SelectValue placeholder={t("common:ph_i̇sletmeSecin_e0f1")} />
                  </SelectTrigger>
                  <SelectContent>
                    {allBusinesses.map((b) => (
                      <SelectItem key={b.business_id} value={b.business_id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="p-3 bg-white rounded-lg border border-red-200">
                <p className="text-sm font-bold text-red-800 mb-2">SİLİNECEK HER ŞEY:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-1 text-xs text-red-700">
                  <div>• Üyeler</div>
                  <div>• Antrenörler</div>
                  <div>• Çalışanlar</div>
                  <div>• Paketler</div>
                  <div>• Ödemeler</div>
                  <div>• Rezervasyonlar</div>
                  <div>• Primler</div>
                  <div>• Maaşlar</div>
                  <div>• Giderler</div>
                  <div>• Ek Ödemeler</div>
                  <div>• Odalar</div>
                  <div>• Takvimler</div>
                  <div>• Formlar</div>
                  <div>• Anketler</div>
                  <div>• Etkinlikler</div>
                  <div>• Yorumlar</div>
                  <div>• Ölçümler</div>
                  <div>• Hedefler</div>
                  <div>• Mesajlar</div>
                  <div>• Bildirimler</div>
                </div>
                <div className="mt-2 pt-2 border-t border-red-200">
                  <p className="text-xs text-green-700 font-bold">✓ SADECE KORUNACAK: İşletme kaydı, ayarları, aboneliği</p>
                </div>
              </div>

              {/* Full Reset Result */}
              {fullResetResult && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    {fullResetResult.message}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                    <div>Üyeler: <span className="font-semibold text-red-600">{fullResetResult.members || 0}</span></div>
                    <div>Antrenörler: <span className="font-semibold text-red-600">{fullResetResult.trainers || 0}</span></div>
                    <div>Çalışanlar: <span className="font-semibold text-red-600">{fullResetResult.employees || 0}</span></div>
                    <div>Paketler: <span className="font-semibold">{fullResetResult.member_packages || 0}</span></div>
                    <div>Ödemeler: <span className="font-semibold">{fullResetResult.payments || 0}</span></div>
                    <div>Rezervasyonlar: <span className="font-semibold">{fullResetResult.reservations || 0}</span></div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {fullResetConfirmStep === 0 && !fullResetResult && (
                <Button
                  onClick={handleFullBusinessReset}
                  className="bg-red-600 hover:bg-red-700 text-white w-full rounded-xl"
                  disabled={!selectedBusinessFull || fullResetLoading}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  İşletmeyi Tam Sıfırla
                </Button>
              )}

              {fullResetConfirmStep === 1 && (
                <div className="flex items-center gap-3">
                  <span className="text-red-700 font-bold">⚠️ Üyeler ve personel SİLİNECEK!</span>
                  <Button
                    onClick={handleFullBusinessReset}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
                  >
                    Devam Et
                  </Button>
                  <Button variant="outline" onClick={resetConfirm} className="rounded-xl">
                    İptal
                  </Button>
                </div>
              )}

              {fullResetConfirmStep === 2 && (
                <div className="flex items-center gap-3">
                  <span className="text-red-700 font-bold">🚨 SON ONAY - GERİ ALINAMAZ!</span>
                  <Button
                    onClick={handleFullBusinessReset}
                    className="bg-red-700 hover:bg-red-800 text-white rounded-xl"
                    disabled={fullResetLoading}
                  >
                    {fullResetLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Onayla ve Sil
                  </Button>
                  <Button variant="outline" onClick={resetConfirm} className="rounded-xl">
                    İptal
                  </Button>
                </div>
              )}

              {fullResetResult && (
                <Button variant="outline" onClick={resetConfirm} className="rounded-xl">
                  Tamam
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Üye Silme - YENİ */}
      <Card className="border-2 border-purple-200 bg-purple-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-purple-700">
            <UserMinus className="w-5 h-5" />
            Kullanıcı Sil
          </CardTitle>
          <CardDescription className="text-purple-600">
            Sistemden bir kullanıcıyı siler. İşletmelerde kayıtlı kullanıcılar silinemez.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingUsers ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          ) : (
            <>
              <div>
                <Label className="text-purple-800">{t("common:kullaniciSecin")}</Label>
                <Select value={selectedUser} onValueChange={(v) => {
                  setSelectedUser(v);
                  setDeleteUserConfirmStep(0);
                  setDeleteUserResult(null);
                  setDeleteUserError(null);
                }}>
                  <SelectTrigger className="w-full mt-1 rounded-xl border-purple-300">
                    <SelectValue placeholder={t("common:ph_kullaniciSecin")} />
                  </SelectTrigger>
                  <SelectContent>
                    {allUsers.map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.name} {u.surname} - {u.email} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="p-3 bg-white rounded-lg border border-purple-200">
                <p className="text-xs text-purple-700">
                  <strong>Not:</strong> Bir işletmede üye, antrenör veya çalışan olarak kayıtlı kullanıcılar silinemez. Önce ilgili işletmeden çıkarılmalıdır.
                </p>
              </div>

              {/* Delete Error - Registration Warning */}
              {deleteUserError && (
                <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-xl">
                  <p className="text-sm font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {deleteUserError.message}
                  </p>
                  <ul className="list-disc list-inside text-sm text-yellow-700">
                    {deleteUserError.registrations?.map((reg, idx) => (
                      <li key={idx}>{reg}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-yellow-600 mt-2">{deleteUserError.suggestion}</p>
                </div>
              )}

              {/* Delete Success */}
              {deleteUserResult && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-sm font-semibold text-green-800 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    {deleteUserResult.message}
                  </p>
                  <p className="text-xs text-green-600 mt-1">Email: {deleteUserResult.email}</p>
                </div>
              )}

              {/* Action Buttons */}
              {deleteUserConfirmStep === 0 && !deleteUserResult && !deleteUserError && (
                <Button
                  onClick={handleDeleteUser}
                  className="bg-purple-600 hover:bg-purple-700 text-white w-full rounded-xl"
                  disabled={!selectedUser || deleteUserLoading}
                >
                  <UserMinus className="w-4 h-4 mr-2" />
                  Kullanıcıyı Sil
                </Button>
              )}

              {deleteUserConfirmStep === 1 && (
                <div className="flex items-center gap-3">
                  <span className="text-purple-700 font-bold">⚠️ Bu işlem geri alınamaz!</span>
                  <Button
                    onClick={handleDeleteUser}
                    className="bg-purple-700 hover:bg-purple-800 text-white rounded-xl"
                    disabled={deleteUserLoading}
                  >
                    {deleteUserLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Onayla ve Sil
                  </Button>
                  <Button variant="outline" onClick={resetConfirm} className="rounded-xl">
                    İptal
                  </Button>
                </div>
              )}

              {(deleteUserResult || deleteUserError) && (
                <Button variant="outline" onClick={resetConfirm} className="rounded-xl">
                  Tamam
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-slate-200"></div>
        </div>
        <div className="relative flex justify-center">
          <span className="bg-white px-4 text-sm text-grey">{t("admin:sistemGeneliSifirlama")}</span>
        </div>
      </div>

      {result ? (
        <Card className="border-2 border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-green-700 font-semibold mb-3">
              <CheckCircle className="w-5 h-5" />
              Sıfırlama Tamamlandı!
            </div>
            <p className="text-green-700 mb-4">{result.message}</p>
            {result.admin_email && (
              <div className="bg-white rounded-xl p-4 border border-green-200 mb-4">
                <p className="text-sm text-grey mb-2">Admin Giriş Bilgileri:</p>
                <p className="font-mono text-sm">Email: {result.admin_email}</p>
                <p className="font-mono text-sm">Şifre: {result.admin_password}</p>
              </div>
            )}
            {result.users_kept && (
              <div className="bg-white rounded-xl p-4 border border-green-200 mb-4">
                <div className="flex items-center gap-2 text-green-700">
                  <Users className="w-4 h-4" />
                  <span className="font-medium">{t("common:kullanicilarVeRolleriKorundu")}</span>
                </div>
                <p className="text-sm text-grey mt-1">İş ilişkileri temizlendi, tekrar kayıt gerekmez</p>
              </div>
            )}
            <Button onClick={resetConfirm} variant="outline" className="rounded-xl">
              Tamam
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Kullanıcılar HARİÇ Sıfırla - ÖNERİLEN */}
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-700">
                <ShieldCheck className="w-5 h-5" />
                Kullanıcılar Hariç Sıfırla
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full ml-2">{t("common:oneri̇len")}</span>
              </CardTitle>
              <CardDescription className="text-blue-600">
                Kullanıcı hesapları korunur, diğer tüm veriler silinir
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {[
                  t("common:businesses"), 
                  t('common:trainers'),
                  t("employees:employees"),
                  t('common:members'),
                  t('common:reservations'),
                  t('common:payments'),
                  t('packages:packages'),
                  t("employees:salaryAccruals"),
                  "Kiralar",
                  "Abonelikler"
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-blue-700">
                    <Trash2 className="w-3 h-3" />
                    {item}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 p-3 bg-green-100 rounded-lg mb-4">
                <Users className="w-4 h-4 text-green-700" />
                <span className="text-sm text-green-700 font-medium">{t("common:kullanicilarKorunacak")}</span>
              </div>

              {confirmStepKeepUsers === 0 && (
                <Button
                  onClick={handleResetKeepUsers}
                  className="bg-blue-600 hover:bg-blue-700 text-white w-full rounded-xl"
                  disabled={loadingKeepUsers}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Kullanıcılar Hariç Sıfırla
                </Button>
              )}

              {confirmStepKeepUsers === 1 && (
                <div className="flex items-center gap-3">
                  <span className="text-blue-700 font-medium">{t('common:areYouSure')}</span>
                  <Button
                    onClick={handleResetKeepUsers}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                  >
                    Evet
                  </Button>
                  <Button variant="outline" onClick={resetConfirm} className="rounded-xl">
                    İptal
                  </Button>
                </div>
              )}

              {confirmStepKeepUsers === 2 && (
                <div className="flex items-center gap-3">
                  <span className="text-blue-700 font-bold">Son onay:</span>
                  <Button
                    onClick={handleResetKeepUsers}
                    className="bg-blue-700 hover:bg-blue-800 text-white rounded-xl"
                    disabled={loadingKeepUsers}
                  >
                    {loadingKeepUsers ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Onayla ve Sıfırla
                  </Button>
                  <Button variant="outline" onClick={resetConfirm} className="rounded-xl">
                    İptal
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* İşletme Tamamen Sil */}
          <Card className="border-2 border-red-300 bg-gradient-to-br from-red-50 to-orange-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <Trash2 className="w-5 h-5" />
                İşletme Tamamen Sil
              </CardTitle>
              <CardDescription className="text-red-600">
                Seçilen işletme ve TÜM ilişkili veriler kalıcı olarak silinir. Hiçbir iz kalmaz!
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* İşletme Seçimi */}
              <div>
                <Label className="text-red-700 font-medium">{t("common:i̇sletmeSec")}</Label>
                <Select value={selectedBusinessDelete} onValueChange={(v) => {
                  setSelectedBusinessDelete(v);
                  setDeleteBusinessConfirmStep(0);
                  setDeleteBusinessResult(null);
                }}>
                  <SelectTrigger className="rounded-xl mt-1 border-red-200">
                    <SelectValue placeholder={t("common:ph_silinecekIsletmeyiSecin")} />
                  </SelectTrigger>
                  <SelectContent>
                    {allBusinesses.map((bus) => (
                      <SelectItem key={bus.business_id} value={bus.business_id}>
                        {bus.name} ({bus.business_id.slice(-6)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Uyarı */}
              <div className="p-4 bg-red-100 rounded-xl border border-red-300">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-700 space-y-1">
                    <p className="font-bold">Bu işlem geri alınamaz! Silinecekler:</p>
                    <ul className="list-disc list-inside space-y-0.5 text-red-600">
                      <li>İşletme kaydı ve ayarları</li>
                      <li>Tüm üyeler ve üyelikleri</li>
                      <li>Tüm antrenörler ve çalışanlar</li>
                      <li>Tüm rezervasyonlar ve talepler</li>
                      <li>Tüm ödemeler ve paketler</li>
                      <li>Tüm mesajlar ve bildirimler</li>
                      <li>Odalar, etkinlikler, anketler...</li>
                      <li>Kullanıcıların bu işletmeyle bağlantısı</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Onay Adımları */}
              {selectedBusinessDelete && (
                <>
                  {deleteBusinessConfirmStep === 0 && (
                    <Button
                      onClick={handleDeleteBusinessCompletely}
                      className="bg-red-600 hover:bg-red-700 text-white w-full rounded-xl"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      İşletmeyi Sil
                    </Button>
                  )}

                  {deleteBusinessConfirmStep === 1 && (
                    <div className="flex items-center gap-3 p-3 bg-red-200 rounded-xl">
                      <AlertTriangle className="w-5 h-5 text-red-700" />
                      <span className="text-red-700 font-medium flex-1">
                        "{allBusinesses.find(b => b.business_id === selectedBusinessDelete)?.name}" silinecek. Emin misiniz?
                      </span>
                      <Button
                        onClick={handleDeleteBusinessCompletely}
                        className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
                      >
                        Evet, Devam
                      </Button>
                      <Button variant="outline" onClick={() => setDeleteBusinessConfirmStep(0)} className="rounded-xl">
                        İptal
                      </Button>
                    </div>
                  )}

                  {deleteBusinessConfirmStep === 2 && (
                    <div className="flex items-center gap-3 p-3 bg-red-300 rounded-xl">
                      <AlertTriangle className="w-5 h-5 text-red-800" />
                      <span className="text-red-800 font-bold flex-1">
                        SON UYARI! Bu işlem geri alınamaz!
                      </span>
                      <Button
                        onClick={handleDeleteBusinessCompletely}
                        className="bg-red-700 hover:bg-red-800 text-white rounded-xl"
                        disabled={deleteBusinessLoading}
                      >
                        {deleteBusinessLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-2" />
                        )}
                        Tamamen Sil
                      </Button>
                      <Button variant="outline" onClick={() => setDeleteBusinessConfirmStep(0)} className="rounded-xl">
                        İptal
                      </Button>
                    </div>
                  )}
                </>
              )}

              {/* Sonuç */}
              {deleteBusinessResult && (
                <div className="p-4 bg-green-100 rounded-xl border border-green-300">
                  <p className="font-bold text-green-700 mb-2">
                    ✅ "{deleteBusinessResult.business_name}" tamamen silindi!
                  </p>
                  <div className="grid grid-cols-3 gap-2 text-sm text-green-600">
                    <div>Üyeler: {deleteBusinessResult.members || 0}</div>
                    <div>Antrenörler: {deleteBusinessResult.trainers || 0}</div>
                    <div>Çalışanlar: {deleteBusinessResult.employees || 0}</div>
                    <div>Rezervasyonlar: {deleteBusinessResult.reservations || 0}</div>
                    <div>Ödemeler: {deleteBusinessResult.payments || 0}</div>
                    <div>Paketler: {deleteBusinessResult.packages || 0}</div>
                    <div>Mesajlar: {deleteBusinessResult.messages || 0}</div>
                    <div>Bildirimler: {deleteBusinessResult.notifications || 0}</div>
                    <div>Kullanıcı Bağlantıları: {deleteBusinessResult.user_business_links || 0}</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* TAM Sıfırla */}
          <Card className="border-2 border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Tam Sıfırlama
              </CardTitle>
              <CardDescription className="text-red-600">
                Kullanıcılar dahil TÜM veriler silinir
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {[
                  t("common:users"),
                  t("common:businesses"), 
                  t('common:trainers'),
                  t('common:members'),
                  t('common:reservations'),
                  t('common:payments'),
                  t('packages:packages'),
                  t('common:rooms'),
                  t('common:messages'),
                  "Bildirimler"
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-red-700">
                    <Trash2 className="w-3 h-3" />
                    {item}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 p-3 bg-red-100 rounded-lg mb-4">
                <AlertTriangle className="w-4 h-4 text-red-700" />
                <span className="text-sm text-red-700 font-medium">Kullanıcılar da SİLİNECEK!</span>
              </div>

              {confirmStep === 0 && (
                <Button
                  onClick={handleReset}
                  className="bg-red-600 hover:bg-red-700 text-white w-full rounded-xl"
                  disabled={loading}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Tam Sıfırlama
                </Button>
              )}

              {confirmStep === 1 && (
                <div className="flex items-center gap-3">
                  <span className="text-red-700 font-medium">{t('common:areYouSure')}</span>
                  <Button
                    onClick={handleReset}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
                  >
                    Evet, Sil
                  </Button>
                  <Button variant="outline" onClick={resetConfirm} className="rounded-xl">
                    İptal
                  </Button>
                </div>
              )}

              {confirmStep === 2 && (
                <div className="flex items-center gap-3">
                  <span className="text-red-700 font-bold">SON UYARI!</span>
                  <Button
                    onClick={handleReset}
                    className="bg-red-700 hover:bg-red-800 text-white rounded-xl"
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Tümünü Sil
                  </Button>
                  <Button variant="outline" onClick={resetConfirm} className="rounded-xl">
                    İptal
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
